// src/tattoo/MobileMorphPanel.jsx
import React from "react";
import { useSnapshot } from "valtio";
import { tattoo, setMorph, setSkinColor } from "./state";



function clamp01(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(1, v));
}

export default function MobileMorphPanel() {
  const snapTattoo = useSnapshot(tattoo);

  const [values, setValues] = React.useState(() => ({})); // { [name]: number }

  // targets: [{ name: "Belly", slots: [{ mesh, index }, ...] }, ...]
  const [targets, setTargets] = React.useState([]);

  const isPoseMorph = React.useCallback((name) => /^POSE__/i.test(name), []);

  const collectMorphTargets = React.useCallback(() => {
    try {
      if (typeof window === "undefined") return;
      const roots = (window.__tattoo_roots || []).filter(Boolean);
      const map = Object.create(null);

      for (const root of roots) {
        root.traverse((node) => {
          if (
            !node ||
            !node.isMesh ||
            !node.morphTargetDictionary ||
            !node.morphTargetInfluences
          ) {
            return;
          }

          const dict = node.morphTargetDictionary;
          const infl = node.morphTargetInfluences;

          for (const [name, index] of Object.entries(dict)) {
            if (isPoseMorph(name)) continue;
            if (typeof index !== "number" || index < 0 || index >= infl.length)
              continue;

            if (!map[name]) map[name] = { name, slots: [] };
            map[name].slots.push({ mesh: node, index });
          }
        });
      }

      setTargets(Object.values(map));
    } catch (e) {
      console.warn("[morph-panel] collect failed", e);
      setTargets([]);
    }
  }, [isPoseMorph]);

  // Initial + delayed collects (covers “model not ready yet” timing)
  React.useEffect(() => {
    collectMorphTargets();
    const timers = [200, 600, 1200].map((ms) =>
      setTimeout(collectMorphTargets, ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [collectMorphTargets]);

  // Listen for explicit recollect requests (eg after model swap)
  React.useEffect(() => {
    const onCollect = () => collectMorphTargets();
    window.addEventListener("morph:collect", onCollect);
    return () => window.removeEventListener("morph:collect", onCollect);
  }, [collectMorphTargets]);

  const currentValueForEntry = React.useCallback((entry) => {
    try {
      if (!entry?.slots?.length) return 0;
      let sum = 0;
      let count = 0;

      for (const slot of entry.slots) {
        const infl = slot.mesh?.morphTargetInfluences;
        if (!infl || slot.index < 0 || slot.index >= infl.length) continue;

        const v = infl[slot.index];
        if (Number.isFinite(v)) {
          sum += v;
          count++;
        }
      }

      return count ? sum / count : 0;
    } catch {
      return 0;
    }
  }, []);

  const setInfluenceForEntry = React.useCallback((entry, value) => {
    const v = clamp01(value);

    try {
      for (const slot of entry.slots) {
        const mesh = slot.mesh;
        const index = slot.index;
        const infl = mesh?.morphTargetInfluences;
        if (!infl || index < 0 || index >= infl.length) continue;

        infl[index] = v;

        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        mats.forEach((m) => m && (m.needsUpdate = true));
      }

      // Persist (your existing state helper)
      setMorph(entry.name, v);

      // Notify anyone who cares (but DO NOT recollect off this event)
      window.dispatchEvent(
        new CustomEvent("morph:updated", { detail: { name: entry.name, value: v } })
      );
    } catch (e) {
      console.warn("[morph-panel] setInfluence failed", e);
    }
  }, []);

  React.useEffect(() => {
    setValues((prev) => {
      const next = { ...prev };
      for (const entry of targets) {
        if (next[entry.name] == null) {
          next[entry.name] = currentValueForEntry(entry);
        }
      }
      return next;
    });
  }, [targets, currentValueForEntry]);

  return (
    <div className="tt-mobile-panel-inner">
      {targets.map((entry) => {
        const uiVal =
          values[entry.name] != null ? values[entry.name] : currentValueForEntry(entry);

        return (
          <label key={entry.name} className="tt-mobile-morph-row" style={{ display: "flex", alignItems: "center", gap: 10, margin: "8px 0", touchAction: "pan-y" }}>
            <span style={{ flex: "0 0 44%", fontSize: 12, color: "#e4e4e4", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {entry.name.replace(/_/g, " ")}
            </span>

            <input
              type="range"
              className="tt-mobile-morph-slider"
              min={0}
              max={1}
              step={0.01}
              value={uiVal}  // ✅ controlled by React state
              onChange={(ev) => {
                const v = clamp01(ev.target.value);

                // ✅ move the thumb immediately
                setValues((prev) => ({ ...prev, [entry.name]: v }));

                // ✅ apply morph to meshes + persist to valtio
                setInfluenceForEntry(entry, v);
              }}
              onPointerDown={(ev) => {
                // Explicit pointer capture: guarantees this input owns
                // the drag from touchdown to release, no matter where
                // the finger drifts. Without this, iOS Safari sometimes
                // hands the pointer to a scrolling ancestor if the
                // finger moves before the input's own drag kicks in
                // — which reads as "have to try a few times to grab it".
                try {
                  ev.currentTarget.setPointerCapture?.(ev.pointerId);
                } catch {}
                ev.currentTarget.classList.add("is-touching");
              }}
              onPointerUp={(ev) => {
                try {
                  ev.currentTarget.releasePointerCapture?.(ev.pointerId);
                } catch {}
                ev.currentTarget.classList.remove("is-touching");
              }}
              onPointerCancel={(ev) => {
                try {
                  ev.currentTarget.releasePointerCapture?.(ev.pointerId);
                } catch {}
                ev.currentTarget.classList.remove("is-touching");
              }}
              style={{ flex: 1 }}
            />

            <span style={{ minWidth: 34, textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 11, opacity: 0.8 }}>
              {uiVal.toFixed(2)}
            </span>
          </label>
        );
      })}

      {/* Skin tone */}
      <div
        style={{
          marginTop: 10,
          paddingTop: 10,
          borderTop: "1px solid #2b2b2b",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#e8e8e8" }}>
            Skin tone
          </div>
          <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>
            Pick a tone so the artist previews the tattoo on you.
          </div>
        </div>

        <input
          type="color"
          value={snapTattoo.skinColor || "#d19771"}
          onChange={(e) => setSkinColor(e.target.value)}
          title="Skin tone"
          style={{
            width: 34,
            height: 34,
            padding: 0,
            border: "1px solid #333",
            borderRadius: 8,
            background: "transparent",
            cursor: "pointer",
          }}
        />
      </div>
    </div>
  );
}